import React, { useState } from 'react';
import { Input, Button, Modal, Spin, Radio } from 'antd';
import { useOfficeSuppliesForm } from './hooks/useOfficeSuppliesForm';
import { calcEndingQty, calcEndingAmt } from '../../services/officeSuppliesApi';
import LookupField from '../../components/LookupField';

const { TextArea } = Input;

const B151Form: React.FC = () => {
  const {
    mode, isEditing, loading,
    records, current, currentIdx,
    fields, setField,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, quickSearch,
  } = useOfficeSuppliesForm();

  const [searchText, setSearchText] = useState('');

  const handleDelete = () => {
    if (!current) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除「${current.fb510_01}」？`,
      okText: '刪除', okType: 'danger', cancelText: '取消',
      onOk: doDelete,
    });
  };

  const handleQuickSearch = () => {
    if (searchText.trim()) quickSearch(searchText.trim());
  };

  const modeLabel = mode === 'add' ? '新增' : mode === 'edit' ? '修改' : '瀏覽';
  const endQty = current ? calcEndingQty(current) : 0;
  const endAmt = current ? calcEndingAmt(current) : 0;

  return (
    <Spin spinning={loading}>
      <div className="max-w-4xl mx-auto p-6">
        <h2 className="text-xl font-bold mb-4">
          事務用品建檔 (pwb151f1)
          <span className="ml-3 text-sm font-normal text-gray-500">
            [{modeLabel}] {records.length > 0 ? `${currentIdx + 1} / ${records.length}` : '無資料'}
          </span>
        </h2>

        {/* Quick Search */}
        <div className="flex items-center gap-2 mb-4">
          <Input placeholder="輸入產品代號查詢" value={searchText}
            onChange={e => setSearchText(e.target.value)} onPressEnter={handleQuickSearch}
            disabled={isEditing} style={{ width: 200 }} maxLength={12} />
          <Button onClick={handleQuickSearch} disabled={isEditing}>查詢</Button>
        </div>

        {/* Navigation */}
        <div className="flex items-center gap-1 mb-4">
          <Button size="small" onClick={navFirst} disabled={isEditing || records.length === 0}>|&lt;</Button>
          <Button size="small" onClick={navPrev} disabled={isEditing || currentIdx <= 0}>&lt;</Button>
          <Button size="small" onClick={navNext} disabled={isEditing || currentIdx >= records.length - 1}>&gt;</Button>
          <Button size="small" onClick={navLast} disabled={isEditing || records.length === 0}>&gt;|</Button>
        </div>

        {/* Form Fields */}
        <div className="grid grid-cols-2 gap-x-8 gap-y-3 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">產品代號</label>
            <Input value={fields.fb510_01} onChange={e => setField('fb510_01', e.target.value)}
              maxLength={12} disabled={mode !== 'add'} style={{ width: 200 }} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">品名</label>
            <Input value={fields.fb510_11} onChange={e => setField('fb510_11', e.target.value)}
              maxLength={40} disabled={!isEditing} style={{ width: 300 }} />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">規格</label>
            <TextArea value={fields.fb510_12} onChange={e => setField('fb510_12', e.target.value)}
              disabled={!isEditing} rows={3} style={{ width: '100%' }} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">產品類別</label>
            <Radio.Group value={fields.fb510_05} onChange={e => setField('fb510_05', e.target.value)}
              disabled={!isEditing}>
              <Radio value="1">消耗品-領用</Radio>
              <Radio value="2">資產-請購</Radio>
            </Radio.Group>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">文具用途</label>
            <Radio.Group value={fields.fb510_06} onChange={e => setField('fb510_06', e.target.value)}
              disabled={!isEditing}>
              <Radio value="1">個人</Radio>
              <Radio value="2">共用</Radio>
            </Radio.Group>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">基本單位</label>
            <Input value={fields.fb510_13} onChange={e => setField('fb510_13', e.target.value)}
              maxLength={10} disabled={!isEditing} style={{ width: 120 }} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">平均單價</label>
            <Input type="number" value={fields.fb510_15} onChange={e => setField('fb510_15', Number(e.target.value))}
              disabled={!isEditing} style={{ width: 160 }} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">安全庫存量</label>
            <Input type="number" value={fields.fb510_20} onChange={e => setField('fb510_20', Number(e.target.value))}
              disabled={!isEditing} style={{ width: 160 }} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">主要供應商</label>
            <LookupField lookupType="supplier" value={fields.fb510_25}
              onChange={v => setField('fb510_25', v)} onCodeConfirm={v => setField('fb510_25', v)}
              maxLength={12} disabled={!isEditing} style={{ width: 160 }} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">貨號</label>
            <Input value={fields.fb510_26} onChange={e => setField('fb510_26', e.target.value)}
              maxLength={12} disabled={!isEditing} style={{ width: 160 }} />
          </div>
        </div>

        {/* Price Info (read-only) */}
        {current && mode === 'browse' && (
          <div className="bg-blue-50 p-4 rounded mb-4">
            <h3 className="text-sm font-bold mb-2">價格資訊</h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>最後採購單價: {current.fb510_16?.toLocaleString('zh-TW', { minimumFractionDigits: 2 })}</div>
              <div>最後採購日期: {current.fb510_18 || '-'}</div>
              <div>最後報價日期: {current.fb510_27 || '-'}</div>
              <div>最後報價單價: {current.fb510_28?.toLocaleString('zh-TW', { minimumFractionDigits: 2 })}</div>
            </div>
          </div>
        )}

        {/* Inventory Summary (read-only) */}
        {current && mode === 'browse' && (
          <div className="bg-gray-50 p-4 rounded mb-4">
            <h3 className="text-sm font-bold mb-2">庫存概況</h3>
            <div className="grid grid-cols-4 gap-2 text-sm">
              <div className="font-medium"></div>
              <div className="font-medium text-center">數量</div>
              <div className="font-medium text-center">金額</div>
              <div></div>
              <div>期初(+)</div>
              <div className="text-right">{current.fb510_30?.toFixed(2)}</div>
              <div className="text-right">{current.fb510_40?.toFixed(2)}</div>
              <div></div>
              <div>採購入庫(+)</div>
              <div className="text-right">{current.fb510_31?.toFixed(2)}</div>
              <div className="text-right">{current.fb510_41?.toFixed(2)}</div>
              <div></div>
              <div>採購退回(-)</div>
              <div className="text-right">{current.fb510_32?.toFixed(2)}</div>
              <div className="text-right">{current.fb510_42?.toFixed(2)}</div>
              <div></div>
              <div>領料單(-)</div>
              <div className="text-right">{current.fb510_35?.toFixed(2)}</div>
              <div className="text-right">{current.fb510_45?.toFixed(2)}</div>
              <div></div>
              <div>退料單(+)</div>
              <div className="text-right">{current.fb510_36?.toFixed(2)}</div>
              <div className="text-right">{current.fb510_46?.toFixed(2)}</div>
              <div></div>
              <div>調整單(+)</div>
              <div className="text-right">{current.fb510_3a?.toFixed(2)}</div>
              <div className="text-right">{current.fb510_4a?.toFixed(2)}</div>
              <div></div>
              <div className="col-span-4 font-bold border-t pt-1 mt-1">
                期末數量: {endQty.toFixed(2)} | 期末金額: {endAmt.toFixed(2)}
              </div>
            </div>
          </div>
        )}

        {/* Audit Fields */}
        {current && mode === 'browse' && (
          <div className="text-xs text-gray-400 mb-4 space-y-1">
            <div>建立人員: {current.fb510_91} | 建立日期: {current.fb510_92 || '-'}</div>
            <div>修改人員: {current.fb510_93} | 現有庫存: {current.fb510_16?.toFixed(2)} | 在途採購: {current.fb510_22?.toFixed(0)}</div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          {!isEditing ? (
            <>
              <Button type="primary" onClick={startAdd}>新增</Button>
              <Button onClick={startEdit} disabled={!current}>修改</Button>
              <Button danger onClick={handleDelete} disabled={!current}>刪除</Button>
            </>
          ) : (
            <>
              <Button type="primary" onClick={save}>儲存</Button>
              <Button onClick={cancel}>取消</Button>
            </>
          )}
        </div>
      </div>
    </Spin>
  );
};

export default B151Form;
